import { Check, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Agent,
  ClientChannelConfig,
  isInstagramClientConfig,
  isWebClientConfig,
  isWidgetClientConfig,
  isExtensionClientConfig,
  isLineClientConfig,
  isMessengerClientConfig,
} from "@/types"

interface ConfigSummaryProps {
  config: ClientChannelConfig
  selectedWidgetAgent?: Agent
}

interface SummaryItemProps {
  label: string
  value?: string | null
  isSet: boolean
  notSetText: string
}

const SummaryItem = ({ label, value, isSet, notSetText }: SummaryItemProps) => (
  <div className="flex items-start gap-2 py-1">
    {isSet ? (
      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <span className="font-medium">{label}:</span>{" "}
      {isSet ? (
        <span className="text-foreground break-words">{value}</span>
      ) : (
        <span className="text-muted-foreground italic">{notSetText}</span>
      )}
    </div>
  </div>
)

export const ConfigSummary = ({
  config,
  selectedWidgetAgent,
}: ConfigSummaryProps) => {
  const { t } = useTranslation()

  const notSetText = t("workspace.channels.publishSummary.notSet", "(Not set)")

  if (isWebClientConfig(config)) {
    const hasTitle = !!config.title?.trim()
    const hasDescriptions =
      config.descriptions && config.descriptions.length > 0
    const hasRedeemCode = !!config.validRedeemCode?.trim()
    const anonymousAccessEnabled = !!config.allowAnonymousAccess
    const hasLoginImage = !!config.loginImageUrl
    const hasHeaderImage = !!config.headerImageUrl
    const hasAdButtons = config.adButtons && config.adButtons.length > 0

    return (
      <div className="space-y-1 text-sm">
        <SummaryItem
          label={t("workspace.channels.publishSummary.title", "Title")}
          value={hasTitle ? `"${config.title}"` : null}
          isSet={hasTitle}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.descriptions",
            "Descriptions"
          )}
          value={
            hasDescriptions
              ? t("workspace.channels.publishSummary.itemCount", {
                  count: config.descriptions.length,
                  defaultValue: `${config.descriptions.length} item(s)`,
                })
              : null
          }
          isSet={hasDescriptions}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.anonymousAccess",
            "Anonymous Access"
          )}
          value={t(
            anonymousAccessEnabled
              ? "workspace.channels.publishSummary.enabled"
              : "workspace.channels.publishSummary.disabled",
            anonymousAccessEnabled ? "Enabled" : "Disabled"
          )}
          isSet={anonymousAccessEnabled}
          notSetText={t(
            "workspace.channels.publishSummary.disabled",
            "Disabled"
          )}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.redeemCode",
            "Redeem Code"
          )}
          value={
            hasRedeemCode
              ? t("workspace.channels.publishSummary.enabled", "Enabled")
              : null
          }
          isSet={hasRedeemCode}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.loginImage",
            "Login Image"
          )}
          value={
            hasLoginImage
              ? t("workspace.channels.publishSummary.uploaded", "Uploaded")
              : null
          }
          isSet={hasLoginImage}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.headerImage",
            "Header Image"
          )}
          value={
            hasHeaderImage
              ? t("workspace.channels.publishSummary.uploaded", "Uploaded")
              : null
          }
          isSet={hasHeaderImage}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.adButtons", "Ad Buttons")}
          value={
            hasAdButtons
              ? t("workspace.channels.publishSummary.itemCount", {
                  count: config.adButtons.length,
                  defaultValue: `${config.adButtons.length} item(s)`,
                })
              : null
          }
          isSet={hasAdButtons}
          notSetText={notSetText}
        />
      </div>
    )
  }

  if (isWidgetClientConfig(config)) {
    const hasTitle = !!config.ui.title?.trim()
    const hasAgent = !!config.agentId?.trim()
    const hasAllowedOrigins = config.allowedOrigins.length > 0
    const hasGreeting = !!config.ui.panel.greeting?.trim()
    const hasLauncherIcon = !!config.ui.launcher.iconUrl?.trim()
    const suggestionsCount =
      selectedWidgetAgent?.suggestedQuestions?.filter((question) =>
        question.trim()
      ).length || 0
    const hasSuggestions = suggestionsCount > 0

    return (
      <div className="space-y-1 text-sm">
        <SummaryItem
          label={t("workspace.channels.publishSummary.title", "Title")}
          value={hasTitle ? `"${config.ui.title}"` : null}
          isSet={hasTitle}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetPosition",
            "Widget Position"
          )}
          value={t(
            config.ui.position === "bottom-left"
              ? "workspace.channels.config.widget.positionBottomLeft"
              : "workspace.channels.config.widget.positionBottomRight"
          )}
          isSet={true}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.widgetAgent", "Agent")}
          value={
            hasAgent
              ? selectedWidgetAgent?.name || config.agentId || null
              : null
          }
          isSet={hasAgent}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetLauncherLabel",
            "Launcher Label"
          )}
          value={config.ui.launcher.label || null}
          isSet={!!config.ui.launcher.label}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetLauncherIcon",
            "Launcher Icon"
          )}
          value={
            hasLauncherIcon
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasLauncherIcon}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetBaseColor",
            "Base Color"
          )}
          value={t(
            `workspace.channels.config.widget.baseColorOptions.${config.ui.theme.baseColor}`
          )}
          isSet={true}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.anonymousAccess",
            "Anonymous Access"
          )}
          value={t(
            config.allowAnonymousAccess
              ? "workspace.channels.publishSummary.enabled"
              : "workspace.channels.publishSummary.disabled",
            config.allowAnonymousAccess ? "Enabled" : "Disabled"
          )}
          isSet={config.allowAnonymousAccess}
          notSetText={t(
            "workspace.channels.publishSummary.disabled",
            "Disabled"
          )}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.widgetLogin", "Login")}
          value={t(
            config.loginEnabled
              ? "workspace.channels.publishSummary.enabled"
              : "workspace.channels.publishSummary.disabled",
            config.loginEnabled ? "Enabled" : "Disabled"
          )}
          isSet={config.loginEnabled}
          notSetText={t(
            "workspace.channels.publishSummary.disabled",
            "Disabled"
          )}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetLoginMode",
            "Login Mode"
          )}
          value={
            config.loginEnabled
              ? config.loginVerificationMode === "direct_unverified"
                ? t(
                    "workspace.channels.config.widget.loginVerificationModeDirect",
                    "Direct (low security)"
                  )
                : t(
                    "workspace.channels.config.widget.loginVerificationModeAssertion",
                    "Assertion"
                  )
              : null
          }
          isSet={config.loginEnabled}
          notSetText={t(
            "workspace.channels.publishSummary.disabled",
            "Disabled"
          )}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetGreeting",
            "Greeting"
          )}
          value={hasGreeting ? config.ui.panel.greeting : null}
          isSet={hasGreeting}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetSuggestions",
            "Suggestions"
          )}
          value={
            hasSuggestions
              ? t("workspace.channels.publishSummary.itemCount", {
                  count: suggestionsCount,
                  defaultValue: `${suggestionsCount} item(s)`,
                })
              : null
          }
          isSet={hasSuggestions}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetOriginPolicy",
            "Origin Policy"
          )}
          value={config.originPolicyMode}
          isSet={true}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.widgetAllowedOrigins",
            "Allowed Origins"
          )}
          value={
            hasAllowedOrigins
              ? t("workspace.channels.publishSummary.itemCount", {
                  count: config.allowedOrigins.length,
                  defaultValue: `${config.allowedOrigins.length} item(s)`,
                })
              : null
          }
          isSet={hasAllowedOrigins}
          notSetText={notSetText}
        />
      </div>
    )
  }

  if (isExtensionClientConfig(config)) {
    const hasExtensionId = !!config.extensionId?.trim()

    return (
      <div className="space-y-1 text-sm">
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.extensionId",
            "Extension ID"
          )}
          value={hasExtensionId ? config.extensionId : null}
          isSet={hasExtensionId}
          notSetText={notSetText}
        />
      </div>
    )
  }

  if (isLineClientConfig(config)) {
    const hasChannelSecret = !!config.channelSecret?.trim()
    const hasChannelAccessToken = !!config.channelAccessToken?.trim()
    const hasLiffId = !!config.liffId?.trim()
    const hasLineLoginChannelId = !!config.lineLoginChannelId?.trim()
    const hasAgentId = !!config.agentId?.trim()

    return (
      <div className="space-y-1 text-sm">
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.lineChannelSecret",
            "Channel Secret"
          )}
          value={
            hasChannelSecret
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasChannelSecret}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.lineChannelAccessToken",
            "Channel Access Token"
          )}
          value={
            hasChannelAccessToken
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasChannelAccessToken}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.lineLiffId", "LIFF ID")}
          value={hasLiffId ? config.liffId : null}
          isSet={hasLiffId}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.lineLoginChannelId",
            "LINE Login Channel ID"
          )}
          value={hasLineLoginChannelId ? config.lineLoginChannelId : null}
          isSet={hasLineLoginChannelId}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.agent", "Agent")}
          value={
            hasAgentId
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAgentId}
          notSetText={notSetText}
        />
      </div>
    )
  }

  if (isMessengerClientConfig(config)) {
    const hasPageId = !!config.pageId?.trim()
    const hasVerifyToken = !!config.webhookVerifyToken?.trim()
    const hasAppSecret = !!config.metaAppSecret?.trim()
    const hasAccessToken = !!config.accessToken?.trim()
    const hasAgentId = !!config.agentId?.trim()

    return (
      <div className="space-y-1 text-sm">
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.messengerPageId",
            "Page ID"
          )}
          value={hasPageId ? config.pageId : null}
          isSet={hasPageId}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.metaWebhookVerifyToken",
            "Webhook Verify Token"
          )}
          value={
            hasVerifyToken
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasVerifyToken}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.metaAppSecret",
            "App Secret"
          )}
          value={
            hasAppSecret
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAppSecret}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.metaAccessToken",
            "Access Token"
          )}
          value={
            hasAccessToken
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAccessToken}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.agent", "Agent")}
          value={
            hasAgentId
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAgentId}
          notSetText={notSetText}
        />
      </div>
    )
  }

  if (isInstagramClientConfig(config)) {
    const hasAccountId = !!config.accountId?.trim()
    const hasVerifyToken = !!config.webhookVerifyToken?.trim()
    const hasAppSecret = !!config.metaAppSecret?.trim()
    const hasAccessToken = !!config.accessToken?.trim()
    const hasAgentId = !!config.agentId?.trim()

    return (
      <div className="space-y-1 text-sm">
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.instagramAccountId",
            "Account ID"
          )}
          value={hasAccountId ? config.accountId : null}
          isSet={hasAccountId}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.metaWebhookVerifyToken",
            "Webhook Verify Token"
          )}
          value={
            hasVerifyToken
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasVerifyToken}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.metaAppSecret",
            "App Secret"
          )}
          value={
            hasAppSecret
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAppSecret}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t(
            "workspace.channels.publishSummary.metaAccessToken",
            "Access Token"
          )}
          value={
            hasAccessToken
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAccessToken}
          notSetText={notSetText}
        />
        <SummaryItem
          label={t("workspace.channels.publishSummary.agent", "Agent")}
          value={
            hasAgentId
              ? t("workspace.channels.publishSummary.configured", "Configured")
              : null
          }
          isSet={hasAgentId}
          notSetText={notSetText}
        />
      </div>
    )
  }

  // Fallback for unknown config types
  return (
    <pre className="text-xs overflow-auto">
      {JSON.stringify(config, null, 2)}
    </pre>
  )
}

export default ConfigSummary
